import { useAuthStore } from "@/store/auth/auth.store";
import { MEETINGS_BACK_URL } from "@/config";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { io } from "socket.io-client";

// autoConnect: false — the socket only connects once an authenticated user is
// available (see the effect below); visitors on /login never open a WebSocket.
const socket = io(MEETINGS_BACK_URL, {
  transports: ["websocket"],
  autoConnect: false,
});

const SocketContext = createContext(socket);

const SocketProvider = ({ children }: PropsWithChildren) => {
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (!user?.personalRoomId || !user?.companyUniqueName) return;

    const joinPayload = {
      personalRoomId: user.personalRoomId,
      companyName: user.companyUniqueName,
    };

    // Join on connect AND on every reconnect so the agent stays subscribed
    // across network drops and server restarts. Without this, a reconnect
    // leaves the agent silent — no waiting-room:join / session.* events flow
    // until the page is refreshed.
    const onConnect = () => {
      socket.emit("agent:join", joinPayload);
    };
    socket.on("connect", onConnect);
    socket.connect();

    return () => {
      socket.off("connect", onConnect);
      // Deliberate client disconnect: the backend treats it as unambiguous
      // and tears the agent down instantly (OFFLINE, rooms left, pending
      // waiting-room entries denied). Covers logout and user switch — the
      // next login reconnects with a fresh socket id, so it can never remain
      // joined to the previous user's rooms.
      socket.disconnect();
    };
  }, [user?.personalRoomId, user?.companyUniqueName]);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export default SocketProvider;

export const useSocketContext = () => useContext(SocketContext);
